import PropTypes from 'prop-types'
import styled from 'styled-components'
import {
  color,
  space,
  typography,
  shadow,
  layout,
  compose,
  variant,
  system,
} from 'styled-system'

import {
  resolveSemanticVariant,
  semanticVariantKeys,
} from './semanticVariants'
import { variants } from './textVariants'

const style = system({
  textTransform: true,
  cursor: true,
  flex: true,
})

const styleText = compose(
  typography,
  color,
  space,
  typography,
  layout,
  shadow,
  style,
)

const variantsSelect = {
  default: {},
  active: {
    textDecoration: 'underline',
    color: 'text.activeText',
  },
}

const SECONDARY_COLOR = 'text.blueLabel'

/**
 * Semantic variants resolve to responsive body* arrays that each set `color`.
 * Those breakpoint media queries override a scalar `color` / `colorVariant` prop
 * on desktop. Expand scalar colors across breakpoints so the prop always wins.
 */
const toBreakpointColor = (value, breakpointCount) => {
  if (value == null || Array.isArray(value)) return value
  return Array.from({ length: breakpointCount + 1 }, () => value)
}

const styledSystemProps = new Set([
  ...typography.propNames,
  ...color.propNames,
  ...space.propNames,
  ...typography.propNames,
  ...layout.propNames,
  ...shadow.propNames,
  ...style.propNames,
])

const customProps = [
  'nowrap',
  'preLine',
  'textTransform',
  'textOverflow',
  'variant',
  'colorVariant',
  'link',
  'ellipsis',
  'active',
  'flexShrink',
  'flexGrow',
  'flexBasis',
  'alignSelf',
]
const propsToFilter = new Set([...styledSystemProps, ...customProps])

const Text = styled.p
  .withConfig({
    shouldForwardProp: (prop) => !propsToFilter.has(prop),
  })
  .attrs(
    ({
      variant = 'body14n',
      color: colorProp,
      colorVariant = 'primary',
      theme,
    }) => {
      const breakpointCount = theme?.breakpoints?.length ?? 3
      const explicitColor =
        colorProp != null
          ? colorProp
          : colorVariant === 'secondary'
            ? SECONDARY_COLOR
            : null

      return {
        variant: resolveSemanticVariant(variant),
        ...(explicitColor != null && {
          color: toBreakpointColor(explicitColor, breakpointCount),
        }),
      }
    },
  )(
  ({ nowrap, preLine, link, ellipsis }) => ({
    ...(preLine && { whiteSpace: 'pre-line' }),
    ...(nowrap && { whiteSpace: 'nowrap' }),
    ...(ellipsis && {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),
    ...(link && {
      cursor: 'pointer',
      '&:hover': { textDecoration: 'underline' },
    }),
  }),
  {
    padding: 0,
    margin: 0,
  },
  variant({
    variants,
  }),
  variant({
    variants: variantsSelect,
    prop: 'active',
  }),
  styleText,
)

const variantProps = [...Object.keys(variants), ...semanticVariantKeys]
const variantPropTypes = PropTypes.oneOf(variantProps)

Text.propTypes = {
  as: PropTypes.node,
  sx: PropTypes.object,
  variant: PropTypes.oneOfType([
    variantPropTypes,
    PropTypes.arrayOf(PropTypes.oneOf(Object.keys(variants))),
  ]),
  colorVariant: PropTypes.oneOf(['primary', 'secondary']),
}

Text.displayName = 'Text'

export default Text
